import React, { FormEvent, useState, useEffect } from "react";
import PhoneInputWithCountrySelect, {
  getCountryCallingCode,
} from "react-phone-number-input";
import { E164Number } from "libphonenumber-js/core";
import "flatpickr/dist/themes/material_blue.css";
import { handleCampaignID } from "@/lib/utils";

interface OptionType {
  value: number | string | null;
  label: string;
}

// Custom component to display country code instead of flag
const CountryCodeDisplay = ({ country }: { country: string }) => {
  const callingCode = country ? getCountryCallingCode(country as any) : "";
  return (
    <span className="inline-block min-w-[30px] text-sm font-medium">
      +{callingCode}
    </span>
  );
};

const WebinarFormExternal: React.FC<{
  containsHeaderTitle?: boolean;
  courseId: number | "";
  submitButtonName: string;
  fromType: "page" | "model";
  webinarId?: number;
  title?: string;
  webinarName?: string;
  webinarDate?: string;
  isOfflineFrom?: boolean;
  cityId?: string | number;
  cityName?: string;
  orientationId?: string | number;
}> = ({
  containsHeaderTitle = false,
  courseId,
  submitButtonName,
  fromType,
  title = "",
  webinarId,
  isOfflineFrom = false,
  cityId,
  orientationId,
  cityName = "Virtual Event",
}) => {
  const enrollInitialFromData = {
    fname: "",
    phone: "",
    email: "",
    course: courseId || "",
    whatsappAllow: 1,
    location: cityName,
    locationId: cityId || "VirtualEvent",
    iAmHereAs: "",
    corporateName: { value: null, label: "Select Company" } as OptionType,
    universityName: { value: null, label: "Select University" } as OptionType,
    educational_qualification: "",
  };

  const Cls: Record<"page" | "model", Record<string, string>> = {
    page: {
      form: "mx-auto flex w-full max-h-[80vh] overflow-y-auto scroll-hidden justify-center flex-wrap flex-row md:max-h-none md:overflow-visible md:scrollbar md:justify-between",
      inputDiv: "md:mt-6 mt-6 relative md:w-[100%] w-[96%]",
      inputLabel:
        "absolute text-[14px] px-1.5 bg-white text-[#A9A9A9] left-[3%] top-[-10px] bg-white z-10",
      input:
        "block w-full rounded-[8px] sf_regular bg-transparent border-[#A9A9A9] border-[1px] pt-1.5 px-4 text-[#030F1C] shadow-sm  h-[48px] placeholder:text-gray-400 focus:border-[#0555AF] sm:text-sm sm:leading-4",
      select: `select_box4 block w-full rounded-[8px] sf_regular bg-transparent border-[#A9A9A9] border-[1px] pt-1.5 px-4 text-[#030F1C] shadow-sm  h-[48px] appearance-none placeholder:text-gray-400 focus:border-[#0555AF] sm:text-sm sm:leading-4`,
      whatsappDiv: "md:w-[100%] w-[96%] my-0",
      whatsappDivChild: "flex items-center justify-start space-x-2.5 mt-4",
      submitBtn:
        "rounded-[8px] bg-[#0555AF] md:text-[18px] text-[16px] font-medium text-white md:py-3 py-3 px-8 mt-4 mb-6",
    },
    model: {
      form: "mx-auto flex justify-between flex-wrap flex-col",
      inputDiv: "md:mt-5 mt-6 relative md:w-[55%] w-[100%]",
      inputLabel:
        "absolute text-[14px] px-1.5 bg-white text-[#A9A9A9] left-[3%] top-[-10px]",
      input:
        "block w-full rounded-[8px] sf_regular bg-transparent border-[#A9A9A9] border-[1px] pt-1.5 px-4 text-[#030F1C] shadow-sm  h-[50px] placeholder:text-gray-400 focus:border-[#0555AF] sm:text-sm sm:leading-4",
      select:
        "select_box4 block w-full rounded-[8px] sf_regular bg-transparent border-[#A9A9A9] border-[1px] pt-1.5 px-4 text-[#030F1C] shadow-sm  h-[50px] appearance-none placeholder:text-gray-400 focus:border-[#0555AF] sm:text-sm sm:leading-4",
      whatsappDiv: "w-[96%]",
      whatsappDivChild: "mt-4 flex items-center space-x-2.5",
      submitBtn:
        "rounded-[8px] bg-[#0555AF] text-[16px] text-semibold text-center text-white py-[10px] px-[24px] mt-6",
    },
  };

  const btnInitalData = {
    text: submitButtonName || "Submit",
    disabled: false,
    show: true,
  };

  const [formData, setFormData] = useState(enrollInitialFromData);
  const [formBtnText, setFormBtnText] = useState(btnInitalData);
  const [step, setStep] = useState(1);
  const [tkId, setTkId] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        const campaignId = handleCampaignID();
        setTkId(campaignId);
      } catch (err: any) {
        console.error(`Error fetching event data: ${err.message}`);
      }
    };

    init();
  }, []);

  const iAmHereAsOptions = [
    { value: "not_enrolled", label: "Yet to Enroll with Miles" },
    { value: "enrolled", label: "Enrolled With Miles" },
  ];

  const [phoneError, setPhoneError] = useState(false);

  const handlePhoneChange = (value?: E164Number | undefined) => {
    setPhoneError(false);
    setFormData((prevData) => ({
      ...prevData,
      phone: value ?? "",
    }));
  };

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.currentTarget;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "whatsappAllow" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleDropdownChange = (e: FormEvent<HTMLSelectElement>) => {
    const { name, value } = e.currentTarget;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateOfflineForm = () => {
    if (formData.iAmHereAs === "corporate" && !formData.corporateName.value) {
      alert("Please select a company.");
      return false;
    }
    if (formData.iAmHereAs === "university" && !formData.universityName.value) {
      alert("Please select a university.");
      return false;
    }

    if (!formData.phone) {
      setPhoneError(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isOfflineFrom && !validateOfflineForm()) {
      return;
    }

    const payload = {
      first_name: formData.fname.split(" ")[0] || "",
      last_name: formData.fname.split(" ")[1] || "",
      phone: formData.phone || null,
      contact_number: formData.phone || null,
      email: formData.email,
      course_id: formData.course || null,
      whatsapp_opt_in: formData.whatsappAllow === 1,
      type: formData.iAmHereAs || null,
      educational_qualification: formData.educational_qualification || null,
      organization_id: formData.corporateName.value || null,
      organization_name: formData.corporateName.label || null,
      college_id: formData.universityName.value || null,
      college_name: formData.universityName.label || null,
      interested_webinar_id: webinarId || null,
      register_for_webinar: !!webinarId,
      page_full_url: window.location.href,
      url_page_path: window.location.pathname,
      campaign_tk: tkId || null,
      city_id: cityId,
      city: cityName,
    };

    if (isOfflineFrom) {
      Object.assign(payload, {
        interested_to_work_in_us: true,
        education_qualification: 1,
        large_event_registration_info: {
          type: formData.iAmHereAs === "enrolled" ? "enrolled" : "not_enrolled",
          orientation_id: orientationId || null,
        },
      });
    }

    try {
      const response = await fetch(
        isOfflineFrom
          ? "/api/eventCms/offlineForm"
          : "/api/eventCms/onlineForm",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("❌ API Error:", errorData);
        alert(
          `Error submitting form: ${
            errorData.message || "Unknown error occurred"
          }`,
        );
        return;
      }

      await response.json();
      setStep(2);
    } catch (error) {
      console.error("❌ Network Error:", error);
      alert(
        "Network error occurred. Please check your connection and try again.",
      );
    } finally {
      setFormBtnText(btnInitalData);
    }
  };

  return (
    <>
      {step === 1 && (
        <form onSubmit={handleSubmit} className={`${Cls[fromType].form}`}>
          {containsHeaderTitle && title && (
            <div className="justify-left md:text-[26px] text-[20px] leading-[1.2] font-semibold pt-6 px-2">
              Register For
              <h2 className="text-[#b41416] md:text-[30px] font-semibold">
                {title}
              </h2>
            </div>
          )}

          {isOfflineFrom && (
            <div className={`${Cls[fromType].inputDiv}`}>
              <label
                htmlFor="iAmHereAs"
                className={`${Cls[fromType].inputLabel}`}
              >
                I am Here As
              </label>
              <select
                name="iAmHereAs"
                value={formData.iAmHereAs}
                onChange={handleDropdownChange}
                className={`${Cls[fromType].select}`}
                required
              >
                <option value="">Select</option>
                {iAmHereAsOptions?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className={`${Cls[fromType].inputDiv}`}>
            <label htmlFor="fname" className={`${Cls[fromType].inputLabel}`}>
              Full Name
            </label>
            <input
              autoComplete="off"
              id="fname"
              name="fname"
              type="text"
              value={formData.fname}
              onChange={handleInputChange}
              className={Cls[fromType].input}
              required
            />
          </div>

          <div className={`${Cls[fromType].inputDiv}`}>
            <label htmlFor="phone" className={`${Cls[fromType].inputLabel}`}>
              Phone Number
            </label>
            <PhoneInputWithCountrySelect
              autoComplete="off"
              defaultCountry="IN"
              value={formData.phone}
              id="phone"
              onChange={(value) =>
                handlePhoneChange(value as E164Number | undefined)
              }
              flagComponent={CountryCodeDisplay}
              className={Cls[fromType].input}
            />
            {phoneError && (
              <p className="text-red-500 text-sm">
                Please fill out this field.
              </p>
            )}
          </div>

          <div className={`${Cls[fromType].inputDiv}`}>
            <label htmlFor="email" className={`${Cls[fromType].inputLabel}`}>
              Email
            </label>
            <input
              autoComplete="off"
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className={Cls[fromType].input}
              required
            />
          </div>

          <div className={`${Cls[fromType].inputDiv}`}>
            <label htmlFor="location" className={`${Cls[fromType].inputLabel}`}>
              City
            </label>
            <input
              type="text"
              id="location"
              value={cityName}
              readOnly
              className={`${Cls[fromType].input}`}
              required
            />
          </div>

          <div className={`${Cls[fromType].whatsappDiv}`}>
            <div className={`${Cls[fromType].whatsappDivChild}`}>
              <input
                id="whatsappAllow"
                name="whatsappAllow"
                type="checkbox"
                defaultChecked={true}
                value={formData.whatsappAllow}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-gray-300 text-[#0555AF]"
              />
              <label htmlFor="whatsappAllow" className="cursor-pointer">
                Yes, connect with me on WhatsApp, Email, Call and SMS
              </label>
            </div>
          </div>

          <button
            type="submit"
            className={`${Cls[fromType].submitBtn} ${
              formBtnText.disabled ? "cursor-not-allowed" : ""
            }`}
          >
            {formBtnText.text}
          </button>
        </form>
      )}

      {step === 2 && (
        <div className="flex justify-center items-center my-8 min-h-[400px]">
          <div className="bg-[#E6F7FF] border border-[#91D5FF] rounded-lg shadow-lg p-6 w-[90%] max-w-[600px] text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto mb-4 text-[#1890FF]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
            <h2 className="text-[#0050B3] font-bold text-xl md:text-2xl mb-4">
              Thank You for Registering!
            </h2>
            <p className="text-[#004085] text-sm md:text-base">
              Your registration was successful.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default WebinarFormExternal;
